import { OrderResponseDto } from 'client/widget-components/ecom/types/EcomSDK';
import { OrderConfirmationStyles, OrderConfirmationTextStyles } from './design';
import { OrderState } from './useOrder';

/**
 * Layout of the shipping/pickup and payment blocks:
 * - ONE_COLUMN: every section stacked vertically (default, legacy behavior).
 * - TWO_COLUMNS: shipping/pickup sits beside payment to shorten the widget.
 */
export type OrderConfirmationLayout = 'ONE_COLUMN' | 'TWO_COLUMNS';

export interface RuntimeOrderConfirmationProps {
    /** Persisted from the widget model; defaults to ONE_COLUMN. */
    layout?: OrderConfirmationLayout;
    /**
     * Site-theme text style per text role, persisted from the widget model.
     * Missing roles fall back to DEFAULT_TEXT_STYLES.
     */
    textStyles?: OrderConfirmationTextStyles;
    /** Colors the design panel persists under the `_styles` model key. */
    _styles?: OrderConfirmationStyles;
    /**
     * "Confirmation intro" content setting. When true (default) the intro
     * header (title, subtitle, invoice number) is shown and the order summary
     * title reads just "Order Summary". When false the header is hidden and the
     * invoice number is appended to the order summary title (legacy behavior).
     * Persisted from the widget model; defaults to true.
     */
    showIntro?: boolean;
    /** @internal For Storybook/testing only — bypasses useOrder when provided */
    _orderState?: OrderState;
}

// Same placeholder used by the Vue snipcart-client demo-mode cart
// (src/demo/data.ts) — an 80×80 gray rect inline SVG, so the mock has no
// external image dependency.
const PLACEHOLDER_IMAGE =
    'data:image/svg+xml,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 width%3D%2280%22 height%3D%2280%22%3E%3Crect width%3D%2280%22 height%3D%2280%22 fill%3D%22%23e5e7eb%22%2F%3E%3C%2Fsvg%3E';

/**
 * A representative order payload exercising fees with tooltips, a discount,
 * mixed line-item types, pickup, custom fields, and a pending custom-payment
 * gateway. Used as the editor-preview fixture and as the Default Storybook
 * story.
 *
 * All PII is synthetic — no real customer data.
 */
export const MOCK_ORDER: OrderResponseDto = {
    is_sandbox_mode: true,
    id: 'order_mock_default',
    email: 'customer@example.com',
    creation_date: '2026-06-01T15:59:22.436Z',
    invoice_number: 'ECOM-1505',
    customer_id: 'cus_mock_customer',
    language: 'en',
    store_url: 'https://example.com/product/glass-coffee-cup',
    has_taxes_included_in_prices: false,
    summary: {
        original_subtotal_amount: null,
        subtotal_amount: 99.96,
        discount_amount: 22.4,
        shipping_amount: 9.99,
        fees: null,
        net_total_amount: null,
        taxes: null,
        total_taxes: 0,
        total_amount: 87.55,
    },
    billing_address: {
        first_name: 'Alex',
        last_name: 'Customer',
        full_name: 'Alex Customer',
        address_1: '100 rue Principale',
        street_number: null,
        street_name: null,
        address_2: null,
        city: 'Montréal',
        region: 'QC',
        sub_locality: null,
        country: 'CA',
        postal_code: 'H2X 1Y4',
        phone: '+1 555 555 0100',
        name: 'Alex Customer',
        surname: 'Customer',
        fullname: 'Alex Customer',
        street_and_number: '100 rue Principale',
        line2: null,
    },
    shipping_address: {
        first_name: 'Alex',
        last_name: 'Customer',
        full_name: 'Alex Customer',
        address_1: '100 rue Principale',
        street_number: null,
        street_name: null,
        address_2: 'Apt 2B',
        city: 'Montréal',
        region: 'QC',
        sub_locality: null,
        country: 'CA',
        postal_code: 'H2X 1Y4',
        phone: '+1 555 555 0100',
        name: 'Alex Customer',
        surname: 'Customer',
        fullname: 'Alex Customer',
        street_and_number: '100 rue Principale',
        line2: 'Apt 2B',
    },
    shipping_instructions: 'Please leave at the front door.',
    pickup: null,
    taxable_total: 87.55,
    total: 87.55,
    currency: 'USD',
    subtotal: 99.96,
    shipment: null,
    discounts: [
        {
            id: 'dis_mock_coupon',
            code: 'COUPON',
            savings: 22.4,
            name: 'Mock coupon',
            discount_type: 'DISCOUNT_CODE',
        },
    ],
    total_discount_amount: 22.4,
    taxes: [],
    total_tax_amount: 0,
    shipping_method: {
        name: 'Standard Shipping',
        cost: 9.99,
        tax_amount: 0,
    },
    items: [
        {
            id: 'item_tshirt',
            type: 'PHYSICAL_PRODUCT',
            unique_id: 'item_tshirt',
            product_id: 'demo-tshirt',
            name: 'Classic T-Shirt',
            description: null,
            sku: 'TS-CLASSIC',
            unit_price: 29.99,
            subtotal: 59.98,
            taxes: [],
            taxable_total: 59.98,
            total: 59.98,
            quantity: 2,
            refunded_quantity: 0,
            dimensions: null,
            weight: null,
            total_weight: null,
            image_url: PLACEHOLDER_IMAGE,
            option_values: [
                { name: 'Size', value: 'Large' },
                { name: 'Color', value: 'Navy' },
            ],
            product_customizations: [
                {
                    id: 'cust_tshirt_text',
                    type: 'TEXT',
                    label: 'Custom text',
                    value: 'Happy Birthday!',
                },
                {
                    id: 'cust_tshirt_engraving',
                    type: 'TEXT',
                    label: 'Engraving',
                    value: 'To Mom — with love',
                    price_modifier: 4.5,
                },
            ],
            payment_plan: null,
            booking: null,
        },
        {
            id: 'item_mug',
            type: 'PHYSICAL_PRODUCT',
            unique_id: 'item_mug',
            product_id: 'demo-mug',
            name: 'Coffee Mug',
            description: null,
            sku: 'MUG-001',
            unit_price: 14.99,
            subtotal: 14.99,
            taxes: [],
            taxable_total: 14.99,
            total: 14.99,
            quantity: 1,
            refunded_quantity: 0,
            dimensions: null,
            weight: null,
            total_weight: null,
            image_url: PLACEHOLDER_IMAGE,
            option_values: [{ name: 'Style', value: 'Glass' }],
            product_customizations: [],
            payment_plan: null,
            booking: null,
        },
        {
            id: 'item_tote',
            type: 'PHYSICAL_PRODUCT',
            unique_id: 'item_tote',
            product_id: 'demo-tote',
            name: 'Tote Bag',
            description: null,
            sku: 'TOTE-001',
            unit_price: 24.99,
            subtotal: 24.99,
            taxes: [],
            taxable_total: 24.99,
            total: 24.99,
            quantity: 1,
            refunded_quantity: 0,
            dimensions: null,
            weight: null,
            total_weight: null,
            image_url: PLACEHOLDER_IMAGE,
            option_values: [{ name: 'Color', value: 'Black' }],
            product_customizations: [],
            payment_plan: null,
            booking: null,
        },
    ],
    status: 'PROCESSED',
    order_status: 'OPEN',
    can_request_cancellation: false,
    can_download_digital_resources: true,
    payment_status: 'PAID',
    payment: {
        method: 'CARD',
        transaction_id: 'txn_mock_default',
        confirmed_method: {
            // Generic credit card: CARD_UNKNOWN icon maps to the default
            // <CreditCard>, and (no display_name set) the name resolves from
            // string admin via nativeEcommRuntime.paymentMethods.CREDIT_CARD.
            name: 'CREDIT_CARD',
            icon_name: 'CARD_UNKNOWN',
            details: '•••• •••• •••• 4242',
        },
        card_last4_digits: '4242',
        card_type: null,
        express_method: null,
        icon_url: null,
        display_name: null,
    },
    refunded_items_amount: 0,
    refunded_taxes_amount: 0,
    total_refunded_amount: 0,
    shipping_refunded: false,
    display_fulfillment_status: 'UNFULFILLED',
    custom_fields: [
        {
            id: 'cf-pickup-time',
            type: 'DATE',
            zone: 'SHIPPING_METHODS',
            label: 'Preferred delivery time',
            value: '2026-06-17T00:00:00.000Z',
            includes_time: false,
        },
        {
            id: 'cf-datetime',
            type: 'DATE',
            zone: 'SHIPPING_METHODS',
            label: 'Date and time',
            value: '2026-06-26T19:04:00.000Z',
            includes_time: true,
        },
        {
            id: 'cf-option',
            type: 'RADIO_LIST',
            zone: 'SHIPPING_ADDRESS',
            label: 'Option',
            value: 'One',
            includes_time: null,
        },
    ],
};

export const MOCK_ORDER_WITH_PICKUP: OrderResponseDto = {
    ...MOCK_ORDER,
    id: 'mock-order-pickup',
    summary: {
        ...MOCK_ORDER.summary,
        shipping_amount: null,
        total_amount: 77.56,
    },
    taxable_total: 77.56,
    total: 77.56,
    shipping_address: null,
    shipping_instructions: null,
    shipping_method: null,
    pickup: {
        id: 'pickup-nyc',
        name: 'NYC pickup',
        instructions: 'Pickup at the front desk, 9am–6pm.',
        address: {
            address_1: '123 Example St.',
            address_2: null,
            city: 'New York',
            postal_code: '10001',
            country: 'US',
            region: 'NY',
            phone: null,
        },
    },
};

export const MOCK_ORDER_PENDING_PAYMENT: OrderResponseDto = {
    ...MOCK_ORDER,
    id: 'mock-order-pending',
    payment_status: 'PENDING',
    payment: {
        method: 'BANK_TRANSFER',
        transaction_id: null,
        confirmed_method: {
            name: 'BANK_TRANSFER',
            display_name: 'Bank transfer',
            icon_name: 'BANK_TRANSFER',
            instructions:
                '<p>Please transfer the total amount to <strong>IBAN: GB29 NWBK 6016 1331 9268 19</strong> within 7 days.</p>',
        },
        card_last4_digits: null,
        card_type: null,
        express_method: null,
        icon_url: null,
        display_name: 'Bank transfer',
    },
};

export const MOCK_ORDER_WITH_BOOKING: OrderResponseDto = {
    ...MOCK_ORDER,
    id: 'mock-order-booking',
    items: [
        {
            ...MOCK_ORDER.items[0],
            id: 'item-booking-1',
            name: 'Yoga session — 60 min',
            type: 'PHYSICAL_PRODUCT',
            booking: {
                id: null,
                seat_id: null,
                slot_start: '2024-02-01T15:00:00Z',
                slot_duration: 60,
                time_zone: 'America/New_York',
                creation_status: 'PENDING',
            },
        },
    ],
};

export const MOCK_ORDER_WITH_DIGITAL_GOODS: OrderResponseDto = {
    ...MOCK_ORDER,
    id: 'mock-order-digital-goods',
    items: [
        {
            ...MOCK_ORDER.items[0],
            id: 'item-ebook',
            type: 'DIGITAL_PRODUCT',
            name: 'The Complete Guide — digital bundle',
            quantity: 1,
            product_customizations: [],
            digital_resources: [
                {
                    reference: '0',
                    title: 'complete-guide.pdf',
                    file_size: 4823040,
                    media_type: 'application/pdf',
                },
                {
                    reference: '1',
                    title: 'worksheets.xlsx',
                    file_size: 76800,
                    media_type:
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
                {
                    reference: '2',
                    title: 'audiobook-chapter-1.mp3',
                    file_size: 31457280,
                    media_type: 'audio/mpeg',
                },
            ],
        },
        MOCK_ORDER.items[1],
    ],
};

export const MOCK_ORDER_DIGITAL_GOODS_UNAVAILABLE: OrderResponseDto = {
    ...MOCK_ORDER_WITH_DIGITAL_GOODS,
    id: 'mock-order-digital-goods-unavailable',
    order_status: 'CANCELLED',
    can_download_digital_resources: false,
};

export const MOCK_ORDER_CANCELLABLE: OrderResponseDto = {
    ...MOCK_ORDER,
    id: 'mock-order-cancellable',
    can_request_cancellation: true,
};

export const MOCK_ORDER_CANCELLATION_REQUESTED: OrderResponseDto = {
    ...MOCK_ORDER,
    id: 'mock-order-cancellation-requested',
    order_status: 'CANCELLATION_REQUESTED',
    can_request_cancellation: false,
};

export const MOCK_ORDER_WITH_CUSTOM_FIELDS: OrderResponseDto = {
    ...MOCK_ORDER,
    id: 'mock-order-custom-fields',
    custom_fields: [
        {
            id: 'cf-1',
            zone: 'CONTACT_INFO',
            type: 'TEXT',
            label: 'Newsletter opt-in',
            value: 'Yes',
        },
        {
            id: 'cf-2',
            zone: 'SHIPPING_ADDRESS',
            type: 'TEXT',
            label: 'Buzzer code',
            value: '#1234',
        },
        {
            id: 'cf-3',
            zone: 'SHIPPING_METHODS',
            type: 'DATE',
            label: 'Preferred delivery date',
            value: '2024-02-10',
        },
        {
            id: 'cf-4',
            zone: 'BILLING_ADDRESS',
            type: 'TEXT',
            label: 'VAT number',
            value: 'GB123456789',
        },
        {
            id: 'cf-5',
            zone: 'PAYMENT_METHODS',
            type: 'CHECKBOX',
            label: 'Save for next time',
            value: 'true',
        },
    ],
};
